import { Gaussian } from './gaussian';
const selfSelector = (i, _) => i;
function mode(set, selector = selfSelector) {
    const groups = Object.groupBy(set, selector);
    let key, max = 0;
    for (let group in groups) {
        const l = groups[group].length;
        if (l > max) {
            key = parseInt(group);
            max = l;
        }
    }
    return key;
}
function median(set, selector = selfSelector) {
    const setCopy = set.slice(0), length = setCopy.length;
    setCopy.sort((a, b) => {
        const i = selector(a), j = selector(b);
        return i - j;
    });
    const index = Math.ceil(setCopy.length / 2) - 1;
    if (length % 2 == 1) {
        return setCopy[index];
    }
    return .5 * (setCopy[index + 1] + setCopy[index]);
}
function sum(set, selector = selfSelector) {
    return set.reduce((p, c, i) => p + selector(c, i), 0);
}
function mean(set, selector = selfSelector) {
    return sum(set, selector) / set.length;
}
function variance(set, selector = selfSelector, bessel = false) {
    const avg = mean(set, selector);
    return set.reduce((p, c) => Math.pow(c - avg, 2) + p, 0) / (set.length - (bessel ? 1 : 0));
}
function stdev(set, selector = selfSelector, bessel = false) {
    const v = variance(set, selector, bessel);
    return Math.sqrt(v);
}
function correlation(set, arg2, selector1 = selfSelector, selector2 = selfSelector) {
    const twoArrays = Array.isArray(arg2);
    if (twoArrays && arg2.length !== set.length) {
        throw new Error('Sets must be of the same length.');
    }
    const length = set.length;
    const setX = set, setY = twoArrays ? arg2 : set;
    const selectorX = twoArrays ? selector1 : arg2;
    const selectorY = twoArrays ? selector2 : selector1;
    const meanX = mean(setX, selectorX);
    const meanY = mean(setY, selectorY);
    let accNum = 0;
    let accDenXSq = 0;
    let accDenYSq = 0;
    for (let i = 0; i < length; i++) {
        const itemX = setX[i], itemY = setY[i];
        const a = selectorX(itemX, i) - meanX;
        const b = selectorY(itemY, i) - meanY;
        accNum += (a * b);
        accDenXSq += (a * a);
        accDenYSq += (b * b);
    }
    return accNum / Math.sqrt(accDenXSq * accDenYSq);
}
function linearRegression(set, arg2, selector1 = selfSelector, selector2 = selfSelector) {
    const twoArrays = Array.isArray(arg2);
    if (twoArrays && arg2.length !== set.length) {
        throw new Error('Sets must be of the same length.');
    }
    const setX = set, setY = twoArrays ? arg2 : set;
    const selectorX0 = twoArrays ? selector1 : arg2;
    const selectorY0 = twoArrays ? selector2 : selectorX0;
    const selectorX = selectorX0 ?? (p => p.x);
    const selectorY = selectorY0 ?? (p => p.y);
    const length = set.length;
    let xyAcc = 0, xAcc = 0, yAcc = 0, x2Acc = 0, y2Acc = 0;
    for (let i = 0; i < length; i++) {
        const itemX = setX[i], itemY = setY[i];
        const x = selectorX(itemX, i);
        const y = selectorY(itemY, i);
        xyAcc += x * y;
        xAcc += x;
        yAcc += y;
        x2Acc += x * x;
        y2Acc += y * y;
    }
    const m = (length * xyAcc - xAcc * yAcc) / (length * x2Acc - Math.pow(xAcc, 2));
    const q = (yAcc - m * xAcc) / length;
    return [m, q];
}
/** Utility tools about statistics, probability, data analysis... */
export class Utils {
    static sum(set, selector) {
        return sum(set, selector);
    }
    static mean(set, selector) {
        return mean(set, selector);
    }
    static median(set, selector) {
        return median(set, selector);
    }
    static mode(set, selector) {
        return mode(set, selector);
    }
    static var(set, selector) {
        return variance(set, selector, true);
    }
    static varp(set, selector) {
        return variance(set, selector, false);
    }
    static stdevp(set, selector) {
        return stdev(set, selector, false);
    }
    static stdev(set, selector) {
        return stdev(set, selector, true);
    }
    static correlation(set, arg2, selector1, selector2) {
        return correlation(set, arg2, selector1, selector2);
    }
    static linearRegression(set, arg2, selector1, selector2) {
        return linearRegression(set, arg2, selector1, selector2);
    }
    // restart from here: https://en.wikipedia.org/wiki/Multivariate_statistics#Multivariate_analysis
    /**
     * Returns a set of utilities for gaussian distribution computations.
     * @param mean
     * @param stdev
     */
    static gaussian(mean, stdev) {
        return new Gaussian(mean, stdev);
    }
}
function searchFnFactory(minAssigner, maxAssigner) {
    const fn = (left, right, f, tolerance) => {
        let min = left;
        let max = right;
        let x1 = minAssigner(min, max);
        let x2 = maxAssigner(min, max);
        let f1 = f(x1);
        let f2 = f(x2);
        while ((max - min) > tolerance) {
            if (f1 < f2) {
                max = x2;
                x2 = x1;
                f2 = f1;
                x1 = minAssigner(min, max);
                f1 = f(x1);
            }
            else {
                min = x1;
                x1 = x2;
                f1 = f2;
                x2 = maxAssigner(min, max);
                f2 = f(x2);
            }
        }
        return .5 * (x1 + x2);
    };
    return fn;
}
const DEFAULT_TOLERANCE = 1e-3;
/**
 * Weight function based on golden ratio.
 */
const goldenRatioSearch = (left, right, weight, tolerance = DEFAULT_TOLERANCE) => {
    const phi = 1.618;
    const minAssigner = (min, max) => max - (max - min) / phi;
    const maxAssigner = (min, max) => min + (max - min) / phi;
    const fn = searchFnFactory(minAssigner, maxAssigner);
    return fn(left, right, weight, tolerance);
};
function linearSearch(segments) {
    if (segments <= 1) {
        throw new RangeError("segments must be a number grater than 1.");
    }
    return (left, right, weight, tolerance = DEFAULT_TOLERANCE) => {
        const minAssigner = (min, max) => min + (max - min) / segments;
        const maxAssigner = (min, max) => max - (max - min) / segments;
        const fn = searchFnFactory(minAssigner, maxAssigner);
        return fn(left, right, weight, tolerance);
    };
}
const gaussianSearch = (left, right, weight, tolerance = DEFAULT_TOLERANCE) => {
    var sigma0 = null;
    var length0 = null;
    const computeSigma = (min, max) => {
        length0 ??= (max - min);
        sigma0 ??= length0 / Math.sqrt(12);
        return sigma0 * (max - min) / length0;
    };
    const minAssigner = (min, max) => Math.max(min, (max + min) / 2 - computeSigma(min, max));
    const maxAssigner = (min, max) => Math.min(max, (max + min) / 2 + computeSigma(min, max));
    const fn = searchFnFactory(minAssigner, maxAssigner);
    return fn(left, right, weight, tolerance);
};
export const SearchFunctions = {
    /**
     * Creates a linear search function.
     * @param segments How many segments to split the range into.
     */
    linear: (segments = 2) => linearSearch(segments),
    /** Golden section search function. */
    goldenRatio: goldenRatioSearch,
    /** Gaussian search function. */
    gaussian: gaussianSearch,
};
//}
